import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { IndividuoArboreo } from "@/mocks/cta";
import TreeStatusBadge from "./TreeStatusBadge";
import TreeRiskBadge from "./TreeRiskBadge";
import TreeReviewBadge from "./TreeReviewBadge";

interface TreeTableProps {
  individuos: IndividuoArboreo[];
}

export default function TreeTable({ individuos }: TreeTableProps) {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="bg-white rounded-card border border-background-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-50">
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Tipo</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Espécie</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Projeto</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Localização</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Fito</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Risco</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Recomendação</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Vistoria</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Técnico</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
              <th className="text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {individuos.map((ind) => (
              <tr key={ind.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                <td className="px-3 py-3">
                  <p className="text-xs font-mono text-foreground-600 whitespace-nowrap">{ind.codigoCta}</p>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md whitespace-nowrap">{ind.tipo}</span>
                </td>
                <td className="px-3 py-3">
                  <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{ind.nomePopular}</p>
                  <p className="text-xs text-foreground-400 italic">{ind.nomeCientifico.split(" ").slice(0, 2).join(" ")}</p>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 max-w-[120px] truncate block">{ind.projeto}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600">{ind.bairro} / {ind.logradouro.split(" ").slice(0, 2).join(" ")}</span>
                </td>
                <td className="px-3 py-3">
                  <TreeStatusBadge status={ind.estadoFitossanitario} />
                </td>
                <td className="px-3 py-3">
                  <TreeRiskBadge risco={ind.classeRisco} />
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 max-w-[140px] truncate block">{ind.recomendacao}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{new Date(ind.ultimaVistoria).toLocaleDateString("pt-BR")}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 whitespace-nowrap">{ind.tecnico}</span>
                </td>
                <td className="px-3 py-3">
                  <TreeReviewBadge status={ind.statusRevisao} />
                </td>
                <td className="px-3 py-3">
                  <div className="relative" ref={openMenuId === ind.id ? menuRef : undefined}>
                    <button
                      onClick={() => setOpenMenuId(openMenuId === ind.id ? null : ind.id)}
                      className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
                      title="Ações"
                    >
                      <i className="ri-more-line text-foreground-600 text-xs"></i>
                    </button>
                    {openMenuId === ind.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg border border-background-200 shadow-lg z-50 py-1 min-w-[160px]">
                        <button onClick={() => { navigate(`/cta/${ind.id}`); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-file-chart-line text-primary-500"></i> Ver FCA
                        </button>
                        <button className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-edit-line text-foreground-500"></i> Editar
                        </button>
                        <button className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-map-2-line text-accent-500"></i> Abrir no mapa
                        </button>
                        <button className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-clipboard-line text-warning-500"></i> Nova vistoria
                        </button>
                        <div className="border-t border-background-200 my-1"></div>
                        <button className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-camera-line text-foreground-500"></i> Fotos ({ind.fotos})
                        </button>
                        <button className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 cursor-pointer">
                          <i className="ri-file-list-3-line text-foreground-500"></i> Documentos
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}